import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

import {
  WebBodyText12,
  WebBodyText16,
} from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import { Button } from '@/components/common/Button';
import FormTextField from '@/components/common/Forms/fields/FormTextField';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/common/Sheet';
import { useSidePanel } from '@/context/SidePanelContext';
import { NewPasswordData, newPasswordSchema } from '../schema';
import useAuthService from '@/hooks/api/useAuthService';

export const ResetPasswordForm = () => {
  const authService = useAuthService();
  const { t } = useTranslation();

  const {
    formState: { errors, isValid, isSubmitting },
    register,
    handleSubmit,
  } = useForm<NewPasswordData>({
    mode: 'onChange',
    resolver: yupResolver(newPasswordSchema),
    defaultValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
  });

  const { closeSidePanel, goTo, sidePanelProps } = useSidePanel();

  const onSubmit = async ({ code, password }: NewPasswordData) => {
    if (!isValid) {
      return;
    }

    try {
      await authService.authServiceConfirmForgotPassword({
        confirmationCode: code,
        password,
        username: sidePanelProps?.username,
      });

      goTo(2);
    } catch (error) {
      goTo(3);
    }
  };

  return (
    <form
      className="flex flex-col gap-5 h-full"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <SheetHeader>
        <SheetTitle>{t('settings.security.changePassword.resetForm.title')}</SheetTitle>
      </SheetHeader>
      <div className="flex flex-col h-full gap-5 justify-between">
        <div className="flex flex-col gap-[20px]">
          <WebBodyText16.Regular className="text-grey72 text-left w-full">
            {t('settings.security.changePassword.resetForm.description')}
          </WebBodyText16.Regular>
          <div className="flex flex-col">
            <FormTextField
              label={t('settings.security.changePassword.resetForm.codeLabel')}
              placeholder={t('settings.security.changePassword.resetForm.codePlaceholder')}
              fieldError={errors.code}
              {...register('code')}
            />
            {!errors.code && (
              <WebBodyText12.Regular className="text-grey72 text-left w-full">
                {t('settings.security.changePassword.resetForm.codeDescription')}
              </WebBodyText12.Regular>
            )}
          </div>
          <div className="flex flex-col">
            <FormTextField
              label={t('settings.security.changePassword.resetForm.passwordLabel')}
              placeholder={t('settings.security.changePassword.resetForm.passwordPlaceholder')}
              fieldError={errors.password}
              type="password"
              {...register('password')}
            />
            {!errors.password && (
              <WebBodyText12.Regular className="text-grey72 text-left w-full">
                {t('settings.security.changePassword.resetForm.passwordDescription')}
              </WebBodyText12.Regular>
            )}
          </div>
          <div className="flex flex-col">
            <FormTextField
              label={t('settings.security.changePassword.resetForm.confirmPasswordLabel')}
              placeholder={t('settings.security.changePassword.resetForm.confirmPasswordPlaceholder')}
              fieldError={errors.confirmPassword}
              type="password"
              {...register('confirmPassword')}
            />
          </div>
        </div>
        <SheetFooter>
          <div className="flex flex-col justify-center items-center w-full gap-5">
            <Button
              className="w-full"
              variant="primary"
              disabled={!isValid || isSubmitting}
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              {t('common.continue')}
            </Button>
            <Button onClick={closeSidePanel} className="w-full" variant="secondary">
              {t('common.cancel')}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </form>
  );
};
